"use client"

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"
//import { connect } from "react-redux"
//import { changeTrack, changePlay } from "@/core/actions/index"
import { db } from "./db/db"
import { motion, AnimatePresence } from "framer-motion"
import { HOME_ANIMATION } from "@/app/pages/homePage/utils/home.constants"
import useOrientation from "@/hooks/useOrientation"
import Topnav from "@/components/topnav"
import { useMusicContext } from "@/context/musicContext"

import {
  Play,
  Pause,
  Trash2,
  Clock,
  Grid3X3,
  List,
  Music4,
} from "lucide-react"

/* =========================
   CONFIG
========================= */

const MAX_SONGS = 10
const MAX_AGE_MS = 3 * 24 * 60 * 60 * 1000

/* =========================
   HELPERS
========================= */

const getRemainingHours = (savedAt: string | Date) => {
  const timeLeft =
    new Date(savedAt).getTime() +
    MAX_AGE_MS -
    Date.now()

  return `${Math.max(
    0,
    Math.floor(timeLeft / 3600000)
  )}h`
}

/* =========================
   ANIMATION
========================= */

const container = {
  hidden: {
    opacity: 0,
    y: 12,
  },

  show: {
    opacity: 1,
    y: 0,
    transition: {
      staggerChildren: 0.04,
    },
  },
}

const item = {
  hidden: {
    opacity: 0,
    y: 8,
  },

  show: {
    opacity: 1,
    y: 0,
  },
}

/* =========================
   COMPONENT
========================= */

function OfflineManager() {
  const {
    isPortrait,
    isLandscape,
    isStandalone,
  } = useOrientation()

  const {
    playSong,
    isPlaying,
    currentTrackData,
    currentPlaylist,
    togglePlayPause,
  } = useMusicContext()

  const [offlineSongs, setOfflineSongs] =
    useState<any[]>([])

  const [view, setView] =
    useState<"grid" | "list">("list")

  /* =========================
     LOAD DB
  ========================= */

  const loadOfflineSongs = useCallback(
    async () => {
      const songs =
        await db.songs
          .where("offline")
          .equals("si")
          .toArray()

      setOfflineSongs(songs)
    },
    []
  )

  useEffect(() => {
    loadOfflineSongs()
  }, [loadOfflineSongs])

 

  /* =========================
   OFFLINE PLAYLIST
========================= */
const offlinePlaylist = useMemo(() => {
  return {
    id: "offline",
    title: "Mis Descargas",
    type: "offline",
    link: "offline",

    playlistData: offlineSongs.map((song) => ({
      id: song.url,

      // NO blob URL aquí
      offlineUrl: song.url,

      songName: song.name,
      songArtist: song.artist,
      songimg: song.songimg,

      blob: song.blob,

      type: "offline",
      offline: true,

      originalUrl: song.url,
    })),
  }
}, [offlineSongs])

/*
 * Liberamos las blob URLs únicamente cuando
 * la playlist deja de existir o el componente
 * se desmonta.
 */
useEffect(() => {
  return () => {
    offlinePlaylist.playlistData.forEach(
      (track: any) => {
        if (
          track.url &&
          track.url.startsWith("blob:")
        ) {
          URL.revokeObjectURL(track.url)
        }
      }
    )
  }
}, [offlinePlaylist])


  /* =========================
   PLAY OFFLINE SONG
========================= */

const handlePlaySong = useCallback(
  (song: any) => {
    const index = offlineSongs.findIndex(
      (item) => item.url === song.url
    )

    if (index === -1) return

    /*
     * Si esta misma canción ya está
     * seleccionada, hacemos play / pause.
     */
    const isCurrent =
      currentPlaylist?.id === "offline" &&
      currentTrackData?.trackKey?.[1] === index

    if (isCurrent) {
      togglePlayPause()
      return
    }

    /*
     * IMPORTANTE:
     *
     * NO creamos ninguna blob URL aquí.
     *
     * Usamos la URL que ya creó
     * offlinePlaylist.
     */
    const track =
      offlinePlaylist.playlistData[index]

    if (!track) return

    playSong(
      track,
      index,
      offlinePlaylist
    )
  },
  [
    offlineSongs,
    offlinePlaylist,
    currentPlaylist,
    currentTrackData,
    togglePlayPause,
    playSong,
  ]
)

  
/* =========================
   PLAY ALL
========================= */

const handlePlayAll = useCallback(() => {
  if (!offlinePlaylist.playlistData.length) {
    return
  }

  /*
   * Si ya estamos dentro de Mis Descargas,
   * simplemente hacemos play / pause.
   */
  if (
    currentPlaylist?.id === "offline"
  ) {
    togglePlayPause()
    return
  }

  /*
   * Usamos directamente la playlist
   * que ya tiene todas sus blob URLs.
   */
  const firstTrack =
    offlinePlaylist.playlistData[0]

  playSong(
    firstTrack,
    0,
    offlinePlaylist
  )
}, [
  offlinePlaylist,
  currentPlaylist,
  togglePlayPause,
  playSong,
])


  /* =========================
   DELETE
========================= */

const deleteSong = async (url: string) => {
  const index = offlineSongs.findIndex(
    (song) => song.url === url
  )

  const isCurrent =
    currentPlaylist?.id === "offline" &&
    currentTrackData?.trackKey?.[1] === index

  if (isCurrent) {
    togglePlayPause()
  }

  await db.songs.delete(url)

  await loadOfflineSongs()
}

  /* =========================
   CURRENT SONG
========================= */

const isCurrent = (song: any) => {
  const index = offlineSongs.findIndex(
    (item) => item.url === song.url
  )

  return (
    currentPlaylist?.id === "offline" &&
    currentTrackData?.trackKey?.[1] === index &&
    isPlaying
  )
}

  /* =========================
     UI STATE
  ========================= */

  const used =
    offlineSongs.length

  const percent =
    Math.min(
      (used / MAX_SONGS) * 100,
      100
    )

  /* =========================
     RENDER
  ========================= */

  return (
    <div
      className="
        relative
        w-full
        h-[100dvh]
        overflow-hidden
        overscroll-none
        touch-pan-y
      "
    >
      <Topnav />

      <div
        className={`
          inset-0
          flex
          w-full
          flex-col
          //overflow-hidden
          select-none
          ${
            isLandscape
              ? "h-[calc(100%-3.7rem)] rounded-xl border border-white/15 bg-[var(--movil-nav)] overscroll-y-auto"
              : ""
          }
          ${isPortrait ? "mb-0 h-[100dvh] pb-0 pt-12" : ""}
          ${isStandalone ? "mb-40 h-[100dvh]" : ""}
        `}
      >
        <div
       
          className={`
            flex-1
            min-h-0
            h-full
            overflow-x-hidden
            overflow-y-scroll
            scrollbar-hide
           
          `}
          style={{
            WebkitOverflowScrolling: "touch",
            overscrollBehaviorY: "none",
            overscrollBehaviorX: "none",
          }}
        >

        {/* =========================
            HEADER
        ========================= */}

        <motion.div variants={HOME_ANIMATION}
                    initial="initial"
                    animate="animate"
                    exit="exit"
          className={`motion-content
            px-4
            pt-3
            w-full
            select-none
            overscroll-none

            ${
              isStandalone
                ? "overscroll-none mt-18"
                : ""
            }
          `}
        >

          <div className="flex items-center justify-between">

            <div>

              <h1 className="text-lg font-semibold flex items-center gap-2">
                <Music4 size={18} />

                Mis Descargas
              </h1>

              <p className="text-xs opacity-50">
                Almacén premium de hasta{" "}
                {MAX_SONGS} pistas
              </p>

            </div>

            <div className="flex gap-2">

              <button
                onClick={() =>
                  setView("list")
                }
                className={`
                  p-2
                  rounded-lg

                  ${
                    view === "list"
                      ? "border border-[var(--soft-border)] bg-[var(--soft-bg)]"
                      : "bg-white/5"
                  }
                `}
              >
                <List size={16} />
              </button>

              <button
                onClick={() =>
                  setView("grid")
                }
                className={`
                  p-2
                  rounded-lg

                  ${
                    view === "grid"
                      ? "border border-[var(--soft-border)] bg-[var(--soft-bg)]"
                      : "bg-white/5"
                  }
                `}
              >
                <Grid3X3 size={16} />
              </button>

            </div>

          </div>

          {/* STORAGE */}

          <div
            className="
              mt-4
              border
              border-[var(--soft-border)]
              bg-[var(--soft-bg)]/100
              rounded-xl
              p-3
            "
          >

            <div
              className="
                flex
                justify-between
                text-xs
                opacity-50
                mb-1
              "
            >
              <span>
                Almacenamiento
              </span>

              <span>
                {used}/{MAX_SONGS}
              </span>
            </div>

            <div
              className="
                h-2
                border
                border-[var(--soft-border)]
                bg-[var(--soft-bg)]
                rounded-full
                overflow-hidden
              "
            >
              <div
                className="
                  h-full
                  bg-gradient-to-r
                  from-emerald-400
                  to-cyan-400
                  transition-all
                "
                style={{
                  width: `${percent}%`,
                }}
              />
            </div>

          </div>

          {/* PLAY ALL */}

          <button
            onClick={handlePlayAll}
            disabled={!offlineSongs.length}
            className="
              w-full
              mt-3
              py-2
              rounded-xl
              border
              border-[var(--soft-border)]
              bg-[var(--primary-color)]/40
              text-[var(--primary-color)]
              font-medium
              active:scale-95
              transition
              disabled:opacity-60
            "
          >
            {currentPlaylist?.id ===
              "offline" &&
            isPlaying
              ? "Pausar lista"
              : "Reproducir lista"}
          </button>

        </motion.div>
      </div>

        {/* =========================
            LIST
        ========================= */}

        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="p-4"
        >

          {offlineSongs.length === 0 ? (

            <div
              className="
                text-center
                opacity-40
                mt-35
              "
            >
              No hay pistas offline
            </div>

          ) : (

            <div
              className={
                view === "grid"
                  ? "grid grid-cols-2 gap-3"
                  : "flex flex-col gap-3"
              }
            >

              <AnimatePresence>

                {offlineSongs.map(
                  (song) => (

                    <motion.div
                      key={song.url}
                      variants={item}
                      className={`
                        rounded-2xl
                        p-3
                        border
                        border-[var(--soft-border)]
                        bg-[var(--soft-bg)]
                        backdrop-blur-md

                        ${
                          view === "grid"
                            ? ""
                            : "flex items-center gap-3"
                        }
                      `}
                    >

                      <img
                        src={
                          song.songimg ||
                          "/placeholder.png"
                        }
                        className="
                          w-14
                          h-14
                          rounded-xl
                          object-cover
                        "
                      />

                      <div className="flex-1">

                        <p className="text-sm font-medium">
                          {song.name}
                        </p>

                        <p className="text-xs opacity-60">
                          {song.artist}
                        </p>

                        <div
                          className="
                            flex
                            items-center
                            gap-1
                            text-[11px]
                            opacity-40
                            mt-1
                          "
                        >
                          <Clock size={12} />

                          {getRemainingHours(
                            song.savedAt
                          )}
                        </div>

                      </div>

                      <div className="flex gap-2">

                        {/* PLAY */}

                        <button
                          onClick={() =>
                            handlePlaySong(song)
                          }
                          className="
                            p-2
                            rounded-full
                            border
                            border-[var(--soft-border)]
                            bg-[var(--soft-bg)]
                            active:scale-90
                            transition
                          "
                        >
                          {isCurrent(song) ? (
                            <Pause size={16} />
                          ) : (
                            <Play size={16} />
                          )}
                        </button>

                        {/* DELETE */}

                        <button
                          onClick={() =>
                            deleteSong(
                              song.url
                            )
                          }
                          className="
                            p-2
                            rounded-full
                            bg-red-500/10
                            text-red-400
                            border
                            border-[var(--soft-border)]
                          "
                        >
                          <Trash2 size={16} />
                        </button>

                      </div>

                    </motion.div>

                  )
                )}

              </AnimatePresence>

            </div>

          )}

        </motion.div>

      </div>
    </div>
  )
}

export default OfflineManager